import { useMemo } from 'react';
import { Plus, Edit, Trash2, CloudUpload } from 'lucide-react';
import { type ColumnDef } from '@tanstack/react-table';
import { Badge, Button, ManagedDataTable, Select } from '@/design-system';
import { StripeSyncBadge } from '@/design-system/billing';
import type { Plan, PlanInterval } from '../types';
import { formatPlanPrice, INTERVAL_LABELS } from '../utils/plan-format';
import { resolveStripeSyncState } from '../utils/stripe-sync';

export type PlansTableHandlers = {
  onEdit: (plan: Plan) => void;
  onDelete: (plan: Plan) => void;
  onSyncStripe: (plan: Plan) => void;
};

type PlansTableProps = {
  plans: Plan[];
  isLoading: boolean;
  search: string;
  onSearchChange: (v: string) => void;
  page: number;
  totalPages: number;
  total: number;
  limit: number;
  onPageChange: (p: number) => void;
  isActiveFilter: boolean | undefined;
  onIsActiveFilterChange: (v: boolean | undefined) => void;
  onNewPlan: () => void;
  handlers: PlansTableHandlers;
  canManagePlans: boolean;
};

export function PlansTable({
  plans,
  isLoading,
  search,
  onSearchChange,
  page,
  totalPages,
  total,
  limit,
  onPageChange,
  isActiveFilter,
  onIsActiveFilterChange,
  onNewPlan,
  handlers,
  canManagePlans,
}: PlansTableProps) {
  const columns = useMemo<ColumnDef<Plan>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nome',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-medium text-gray-900">{row.original.name}</div>
            {row.original.description && (
              <div className="text-xs text-gray-500 mt-1">{row.original.description}</div>
            )}
          </div>
        ),
      },
      {
        accessorKey: 'price',
        header: 'Preço por licença',
        cell: ({ row }) => (
          <div>
            <div className="text-sm font-medium text-gray-900">
              {formatPlanPrice(row.original.price, row.original.currency)}
            </div>
          </div>
        ),
      },
      {
        accessorKey: 'interval',
        header: 'Recorrência',
        cell: ({ row }) => (
          <Badge>
            {INTERVAL_LABELS[(row.original.interval as PlanInterval) || 'month']}
          </Badge>
        ),
      },
      {
        accessorKey: 'activeSubscriptionsCount',
        header: 'Assinaturas',
        cell: ({ row }) => (
          <span className="text-sm text-gray-900">{row.original.activeSubscriptionsCount ?? 0}</span>
        ),
      },
      {
        accessorKey: 'isActive',
        header: 'Status',
        cell: ({ row }) => (
            <span
              className={`px-2 py-1 text-xs font-medium rounded-full ${
                row.original.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
              }`}
            >
              {row.original.isActive ? 'Ativo' : 'Inativo'}
            </span>
        ),
      },
      {
        id: 'stripe',
        header: 'Stripe',
        cell: ({ row }) => {
          const state = resolveStripeSyncState(row.original);
          return (
            <div className="flex flex-col items-start gap-1">
              <StripeSyncBadge state={state} />
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Ações',
        cell: ({ row }) => {
          if (!canManagePlans) return <span className="text-xs text-gray-500">Somente leitura</span>;
          const canSync = (row.original.pricesCount ?? 0) > 0;
          return (
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => handlers.onEdit(row.original)}
                className="p-1 text-gray-400 hover:text-gray-600"
                title="Editar"
              >
                <Edit size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => canSync && handlers.onSyncStripe(row.original)}
                className="p-1 text-gray-400 hover:text-purple-600 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-gray-400"
                disabled={!canSync}
                title={
                  canSync ? 'Sincronizar com Stripe' : 'Cadastre ao menos um preço por moeda antes de sincronizar'
                }
              >
                <CloudUpload size={16} />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={() => handlers.onDelete(row.original)}
                className="p-1 text-gray-400 hover:text-red-600"
                title="Excluir"
              >
                <Trash2 size={16} />
              </Button>
            </div>
          );
        },
      },
    ],
    [handlers, canManagePlans]
  );

  return (
    <div className="space-y-4">
      <ManagedDataTable
        title="Planos"
        columns={columns}
        data={plans}
        loading={isLoading}
        searchPlaceholder="Buscar por nome ou slug..."
        searchValue={search}
        onSearchChange={onSearchChange}
        toolbarFilters={<div className="w-full sm:w-44"><Select
          label="Status"
          aria-label="Filtrar planos por status"
          value={isActiveFilter === undefined ? '' : String(isActiveFilter)}
          onChange={(e) => onIsActiveFilterChange(e.target.value === '' ? undefined : e.target.value === 'true')}
        >
          <option value="">Todos os status</option>
          <option value="true">Ativos</option>
          <option value="false">Inativos</option>
        </Select></div>}
        toolbarButtons={canManagePlans ? [
          {
            label: 'Novo Plano',
            icon: <Plus size={16} />,
            onClick: onNewPlan,
          },
        ] : []}
        pagination={{ page, totalPages, total, limit, onPageChange }}
        emptyMessage="Nenhum plano encontrado."
      />
    </div>
  );
}
